import React, { useContext, useEffect, useState } from 'react';
import { useClub } from '../context/ClubContext';
import { getClubDetails } from '../api'; // Import your API function
import { EnvelopeIcon, PhoneIcon, MapPinIcon, GlobeAltIcon } from '@heroicons/react/24/outline';
import { FaFacebook, FaInstagram, FaTwitter, FaWhatsapp, FaXTwitter, FaYoutube } from 'react-icons/fa6';

const ClubContacts = () => {
  const { clubDetails, setClubDetails } = useClub();
  const [isLoading, setIsLoading] = useState(true);
  
  useEffect(() => {
    const fetchClubDetails = async () => {
      try {
        setIsLoading(true);
        const details = await getClubDetails();
        setClubDetails(details);
      } catch (error) {
        console.error('Failed to fetch club details:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchClubDetails();
  }, [setClubDetails]);

  if (isLoading || !clubDetails) {
    return (
      <div className="p-4 bg-slate-100 min-h-screen">
        <div className="shadow-md rounded-lg bg-white p-6 flex items-center justify-center text-slate-600">
          <div className="flex items-center gap-3">
            <svg className="animate-spin h-6 w-6 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span>Loading club details...</span>
          </div>
        </div>
      </div>
    );
  }

  // Separate contacts
  const phoneContacts = (clubDetails.contacts || []).filter(c => c.phone);
  const emailContacts = (clubDetails.contacts || []).filter(c => c.email);

  const whatsappHref = (phone) => {
    const digits = String(phone || '').replace(/\D/g, '');
    return digits ? `https://wa.me/${digits}` : null;
  };

  return (
    <div className="max-w-2xl mx-auto bg-white rounded-lg shadow p-6 mt-6">
      {/* Club Name */}
      <h1 className="text-3xl font-bold text-slate-800 mb-2 flex items-center gap-2">
        {clubDetails.name}
      </h1>
      {/* Address */}
      {clubDetails.location && (
        <div className="flex items-center text-slate-600 mb-2">
          <MapPinIcon className="h-5 w-5 mr-2 text-blue-500" />
          <span>{clubDetails.location}</span>
        </div>
      )}
      {/* Website */}
      {clubDetails.website && (
        <div className="flex items-center text-slate-600 mb-2">
          <GlobeAltIcon className="h-5 w-5 mr-2 text-green-600" />
          <a href={clubDetails.website} target="_blank" rel="noopener noreferrer" className="hover:underline text-blue-700">{clubDetails.website}</a>
        </div>
      )}
      {/* Social Media */}
      <div className="flex items-center gap-4 mb-4 mt-2">
        {clubDetails.facebook && (
          <a href={clubDetails.facebook} target="_blank" rel="noopener noreferrer" title="Facebook">
            <FaFacebook className="h-6 w-6 text-blue-600 hover:text-blue-800" />
          </a>
        )}
        {clubDetails.instagram && (
          <a href={clubDetails.instagram} target="_blank" rel="noopener noreferrer" title="Instagram">
            <FaInstagram className="h-6 w-6 text-pink-500 hover:text-pink-700" />
          </a>
        )}
        {clubDetails.twitter && (
          <a href={clubDetails.twitter} target="_blank" rel="noopener noreferrer" title="Twitter">
            <FaTwitter className="h-6 w-6 text-blue-400 hover:text-blue-600" />
          </a>
        )}
        {clubDetails.xdotcom && (
          <a href={clubDetails.xdotcom} target="_blank" rel="noopener noreferrer" title="X">
            <FaXTwitter className="h-6 w-6 text-black hover:text-slate-600" />
          </a>
        )}
        {clubDetails.youtube && (
          <a href={clubDetails.youtube} target="_blank" rel="noopener noreferrer" title="YouTube">
            <FaYoutube className="h-6 w-6 text-red-600 hover:text-red-800" />
          </a>
        )}
      </div>
      {/* Phone Contacts */}
      <div className="grid grid-cols-1 md:grid-cols-1 gap-6 mt-6">
        {phoneContacts.length > 0 && (
          <div>
            <h2 className="text-lg font-semibold flex items-center gap-2 mb-2">
              <PhoneIcon className="h-5 w-5 text-green-600" /> Phone Contacts
            </h2>
            <ul className="space-y-2">
              {phoneContacts.map((c, idx) => {
                const waUrl = c.whatsapp === true ? whatsappHref(c.phone) : null;
                return (
                  <li key={idx} className="flex items-center gap-2 text-slate-600 flex-wrap">
                    <span className="font-medium">{c.name}:</span>
                    <a href={`tel:${c.phone}`} className="hover:underline text-blue-700">{c.phone}</a>
                    {waUrl && (
                      <a
                        href={waUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Chat on WhatsApp"
                        className="inline-flex text-green-600 hover:text-green-700"
                      >
                        <FaWhatsapp className="h-6 w-6" aria-hidden />
                        <span className="sr-only">WhatsApp</span>
                      </a>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-1 gap-6 mt-6">
        {/* Email Contacts */}
        {emailContacts.length > 0 && (
          <div>
            <h2 className="text-lg font-semibold flex items-center gap-2 mb-2">
              <EnvelopeIcon className="h-5 w-5 text-blue-600" /> Email Contacts
            </h2>
            <ul className="space-y-2">
              {emailContacts.map((c, idx) => (
                <li key={idx} className="flex items-center gap-2 text-slate-600">
                  <span className="font-medium">{c.name}:</span>
                  <a href={`mailto:${c.email}`} className="hover:underline text-blue-700">{c.email}</a>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      {/* Google Map Embed */}
      <div className="mt-6">
        {clubDetails.googleMapEmbedUrl && (
          <iframe 
            src={clubDetails.googleMapEmbedUrl}
            width="100%" 
            height="350" 
            style={{ border: 0 }} 
            allowFullScreen 
            loading="lazy" 
            referrerPolicy="no-referrer-when-downgrade">
          </iframe>
        )}
      </div>
    </div>
  );
};

export default ClubContacts;
